<div class="pm" *ngIf="profile$ | async as profile" role="document" aria-labelledby="profile-menu-name">
  <!-- Who you are + company code -->
  <div class="pm-head">
    <div class="pm-identity">
      <span class="pm-avatar">
        <img *ngIf="profile.avatar?.length; else initialsTpl" src="{{ mediaUrl }}{{ profile.avatar[0].fileName }}" alt="" />
        <ng-template #initialsTpl>{{ initials(profile.name) }}</ng-template>
      </span>
      <div class="pm-who">
        <h2 id="profile-menu-name">{{ profile.name }}</h2>
        <span
          >{{ profile.profileType }}<ng-container *ngIf="company$ | async as company"> · {{ company.name }}</ng-container></span
        >
      </div>
      <button type="button" class="fd-icon-btn pm-close" aria-label="Close profile menu" (click)="dismiss()">
        <fa-icon [icon]="['far', 'xmark']"></fa-icon>
      </button>
    </div>

    <div *ngIf="company$ | async as company" class="pm-code" data-testid="profile-company-code">
      <div>
        <span class="pm-code-label">Company code</span>
        <span class="pm-code-value select-all">{{ company.code }}</span>
      </div>
      <button type="button" class="fd-btn fd-btn--ghost fd-btn--sm" (click)="copyCompanyCode(company.code)" aria-label="Copy company code" data-testid="profile-copy-code">
        <fa-icon [icon]="['far', 'copy']"></fa-icon> Copy
      </button>
    </div>
  </div>

  <div class="pm-body">
    <nav aria-label="Account" class="pm-nav">
      <button type="button" class="pm-row" (click)="openProfileSection('profile')">
        <span class="pm-row-icon"><fa-icon [icon]="['far', 'user']"></fa-icon></span>
        <span class="pm-row-text">
          <span class="pm-row-title">Profile details</span>
          <span class="pm-row-desc">Your name, contact details, avatar and sign-in settings</span>
        </span>
        <fa-icon class="pm-row-chevron" [icon]="['far', 'chevron-right']"></fa-icon>
      </button>
      <button *ngIf="hasRole('entityAdmin')" type="button" class="pm-row" (click)="openProfileSection('company')">
        <span class="pm-row-icon"><fa-icon [icon]="['far', 'building']"></fa-icon></span>
        <span class="pm-row-text">
          <span class="pm-row-title">Company details</span>
          <span class="pm-row-desc">Company name, address, logo and contact information</span>
        </span>
        <fa-icon class="pm-row-chevron" [icon]="['far', 'chevron-right']"></fa-icon>
      </button>
      <button type="button" class="pm-row" (click)="resetPassword()">
        <span class="pm-row-icon"><fa-icon [icon]="['far', 'key']"></fa-icon></span>
        <span class="pm-row-text">
          <span class="pm-row-title">Reset password</span>
          <span class="pm-row-desc">We'll email you a link to set a new one</span>
        </span>
        <fa-icon class="pm-row-chevron" [icon]="['far', 'chevron-right']"></fa-icon>
      </button>
    </nav>

    <ng-container *ngIf="messages$ | async as messages">
      <section class="pm-messages" aria-labelledby="profile-menu-messages" *ngIf="hasRole('messageService') || receivedMessages(messages).length > 0">
        <div class="pm-section-head">
          <h3 id="profile-menu-messages">Messages</h3>
          <span class="pm-count" *ngIf="unreadCount(messages) > 0">{{ unreadCount(messages) }}</span>
          <button
            *ngIf="pendingShareRequests(messages).length >= 2"
            type="button"
            class="ml-auto fd-btn fd-btn--outline fd-btn--positive fd-btn--sm"
            [disabled]="acceptingAllShares"
            [attr.aria-busy]="acceptingAllShares || null"
            (click)="acceptAllShareRequests(messages)"
          >
            <fa-icon [icon]="['far', 'check']"></fa-icon>
            Accept all share requests ({{ pendingShareRequests(messages).length }})
          </button>
        </div>

        <div class="pm-list">
          <div class="pm-message" *ngFor="let message of receivedMessages(messages)" [class.read]="message.msgRead">
            <span class="pm-dot" [class.unread]="!message.msgRead"></span>
            <div class="pm-message-text">
              <span class="pm-message-subject"
                >{{ message.subject }}<span class="sr-only" *ngIf="!message.msgRead"> (unread)</span></span
              >
              <span class="pm-message-body">{{ message.message }}</span>
            </div>
            <div class="pm-message-actions">
              <button
                *ngIf="message.msgType === MessageType.JobScheduleShared && message.status !== 'Complete' && (hasRole('jobSchedules') || profile?.jobSchedulesEnabled)"
                type="button"
                class="fd-btn fd-btn--outline fd-btn--sm"
                (click)="openJobSchedule(message)"
                [attr.data-testid]="'message-open-job-schedule-' + message.data.worklistId"
              >
                Open
              </button>
              <button
                *ngIf="(message.msgType === MessageType.ReportRequest || message.msgType === MessageType.Reporting) && message.status !== 'Complete'"
                type="button"
                class="fd-btn fd-btn--outline fd-btn--sm"
                (click)="goToReportRequest(message)"
              >
                View
              </button>
              <ng-container *ngIf="message.msgType === MessageType.TransferRequest || message.msgType === MessageType.ShareRequest">
                <button *ngIf="message.status !== 'Complete'" type="button" class="fd-btn fd-btn--outline fd-btn--positive fd-btn--sm" (click)="processMessage(message)">Accept</button>
                <button *ngIf="!message.msgRead" type="button" class="fd-icon-btn fd-icon-btn--sm fd-icon-btn--danger" [attr.aria-label]="'Delete message: ' + message.subject" (click)="trashMessage(message)">
                  <fa-icon [icon]="['far', 'trash']"></fa-icon>
                </button>
              </ng-container>
            </div>
          </div>
          <p class="pm-empty" *ngIf="receivedMessages(messages).length === 0">No messages</p>
        </div>
      </section>

      <section class="pm-messages" aria-labelledby="profile-menu-sent" *ngIf="hasRole('messageService')">
        <div class="pm-section-head">
          <h3 id="profile-menu-sent">Sent messages</h3>
        </div>
        <div class="pm-list">
          <div class="pm-message" *ngFor="let message of messages.messagesFrom" [class.read]="message.msgRead">
            <span class="pm-dot"></span>
            <div class="pm-message-text">
              <span class="pm-message-subject">{{ message.subject }}</span>
              <span class="pm-message-body">{{ message.message }}</span>
            </div>
            <div class="pm-message-actions">
              <button *ngIf="!message.msgRead" type="button" class="fd-icon-btn fd-icon-btn--sm fd-icon-btn--danger" [attr.aria-label]="'Delete message: ' + message.subject" (click)="trashMessage(message)">
                <fa-icon [icon]="['far', 'trash']"></fa-icon>
              </button>
            </div>
          </div>
          <p class="pm-empty" *ngIf="!messages.messagesFrom?.length">No messages</p>
        </div>
      </section>
    </ng-container>
  </div>

  <div class="pm-foot">
    <div class="pm-foot-meta">
      <button type="button" class="fd-btn fd-btn--ghost fd-btn--sm" (click)="whatsNew()" data-testid="profile-whats-new">What's new in {{ currentApplicationVersion }}</button>
      <span class="pm-meta" [class.warn]="cacheSizeMB > 20">
        Cache: {{ cacheSizeMB }} MB<ng-container *ngIf="uuidFoldersToDelete.length > 0"> ({{ uuidFoldersToDelete.length }} folders)</ng-container>
        <button *ngIf="cacheSizeMB > 20" type="button" class="fd-btn fd-btn--ghost fd-btn--danger fd-btn--sm" (click)="clearCacheUuidFolders()">Clear cache</button>
      </span>
      <span class="pm-meta" *ngIf="hasRole('debug')">
        <button type="button" class="fd-btn fd-btn--ghost fd-btn--sm" (click)="viewLog()">Console log</button>
        <button type="button" class="fd-btn fd-btn--ghost fd-btn--sm" (click)="viewDebug()">Debug data</button>
      </span>
    </div>
    <button type="button" class="fd-btn fd-btn--ghost fd-btn--danger" (click)="logOut()">
      <fa-icon [icon]="['far', 'arrow-right-from-bracket']"></fa-icon>
      Log out
    </button>
  </div>
</div>
